import React, {
  FC,
  HTMLProps,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import AliceCarousel, { Props as AliceProps } from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";
import "./index.scss";
import { AppConfig } from "../../config";

// its used for mobile displaying products,
// so I placed all settings in here not caring about component flexibility

interface CarouselProps extends Omit<Partial<AliceProps>, "children"> {
  perPageLoad?: number;
  selectedQuantity?: number; // just to updateHeight, don't know how to use ResizeObserver correctly here :(
  canLoadMore?: boolean;
}

interface Props extends HTMLProps<HTMLDivElement> {
  carouselProps?: CarouselProps;
  children: ReactNode;
}

const ProductsMobileCarousel: FC<Props> = ({
  carouselProps,
  children,
  ...props
}) => {
  const container = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const slides = React.Children.map(children, (child, idx) => {
    if (React.isValidElement(child)) return <div key={idx}>{child}</div>;
    return null;
  }) as ReactElement[];

  useEffect(() => {
    if (carouselProps?.perPageLoad) {
      setActiveIndex(slides.length - carouselProps.perPageLoad - 1);
    }
    // wait for html to be rendered
    setTimeout(() => {
      updateHeight();
    }, 500);
  }, [slides.length, carouselProps?.perPageLoad]);

  useEffect(() => {
    setTimeout(() => {
      updateHeight();
    }, 500);
  }, [carouselProps?.selectedQuantity]);

  const updateHeight = () => {
    if (container.current) {
      const cont = container.current;
      const list = cont?.querySelector(".alice-carousel__wrapper");
      const activeSlides = list?.querySelectorAll(".__active > div");

      if (cont && list && activeSlides && activeSlides.length > 0) {
        let maxHeight = 0;
        activeSlides.forEach((slide) => {
          const height = slide.getBoundingClientRect().height;
          if (height > maxHeight) maxHeight = height;
        });

        if (maxHeight !== 0) {
          (list as HTMLElement).style.height = maxHeight + "px";
        }
      }
    }
  };

  return (
    <div
      ref={(v) => {
        container.current = v;
      }}
      {...props}
    >
      <AliceCarousel
        mouseTracking={true}
        paddingLeft={30}
        paddingRight={30}
        autoHeight={false}
        autoPlay={false}
        controlsStrategy={"responsive"}
        infinite={false}
        responsive={{
          0: {
            items: 1,
          },
          480: {
            items: 2,
          },
        }}
        disableDotsControls={true}
        disableButtonsControls={AppConfig.isTouchDevice}
        {...carouselProps}
        renderNextButton={({ isDisabled }) => {
          // just to prevent it being disabled and have ability to click to load more
          return (
            <div className="alice-carousel__next-btn">
              <div className="alice-carousel__next-btn-wrapper">
                <p
                  className={`alice-carousel__next-btn-item ${
                    isDisabled && !carouselProps?.canLoadMore && "__inactive"
                  }`}
                >
                  <span data-area=">" />
                </p>
              </div>
            </div>
          );
        }}
        activeIndex={activeIndex}
        onSlideChange={(e) => {
          carouselProps?.onSlideChange?.(e);
        }}
        onSlideChanged={(e) => {
          setActiveIndex(e.item);
          updateHeight();
          carouselProps?.onSlideChanged?.(e);
        }}
        onInitialized={(e) => {
          setTimeout(() => {
            updateHeight();
          }, 100);
          carouselProps?.onInitialized?.(e);
        }}
        items={slides}
      />
    </div>
  );
};

export default ProductsMobileCarousel;
